import { useState } from "react";
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Button,
  Badge,
  VStack,
  HStack,
  Icon,
  chakra,
  SimpleGrid,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon,
  SmallCloseIcon,
  ArrowLeftIcon,
  StarIcon,
  LockIcon,
  RepeatIcon,
  ChatIcon,
} from "@chakra-ui/icons";
import { UserProfile } from "layout/profile";
import { BoxPanel } from "components/Panels/BoxPanel";

const MotionBox = motion(Box);
const MotionGridItem = motion(GridItem);

// ----------------------------------------------------------------
// انیمیشن‌ها
// ----------------------------------------------------------------
const shine = keyframes`
  0% { left: -120%; }
  35% { left: 130%; }
  100% { left: 130%; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-14px); }
`;

const cardEntrance = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const priceSwap = {
  initial: { opacity: 0, y: -8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
};

const MotionVStack = motion(VStack);
const heroStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  },
};
const badgePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(63,189,176,.35); }
  50% { box-shadow: 0 0 0 6px rgba(63,189,176,0); }
`;

// ----------------------------------------------------------------
// کمکی: فرمت عدد قیمت (رشته‌ی دارای کاما) و محاسبه‌ی معادل ماهانه
// ----------------------------------------------------------------
const toNumber = (priceStr) => {
  const n = Number(String(priceStr).replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const formatToman = (n) => n.toLocaleString("en-US");

// ----------------------------------------------------------------
// داده پلن‌ها
// ----------------------------------------------------------------
const PLAN_DATA = [
  {
    key: "pro",
    tier: "مناسب کسب‌وکارهای نوپا",
    title: "پایه",
    desc: "شروع مطمئن برای شرکت‌های کوچک و در حال رشد با چند طرف‌حساب فعال",
    dotColor: "plan_plus_bg",
    priceYearly: "4,000,000",
    featured: false,
    dark: false,
    ctaLabel: "انتخاب پلن پایه",
    featuresHead: "امکانات شامل",
    features: [
      { text: "ثبت نامحدود صورتحساب الکترونیکی", on: true },
      { text: "مدیریت طرف‌حساب نامحدود", on: true },
      { text: "تا ۵ شرکت فعال هم‌زمان", on: true },
      { text: "صدور گواهی CSR", on: true },
      { text: "دسترسی تا ۵ کاربر هم‌زمان", on: true },
      { text: "پشتیبانی تلفنی", on: true },
    ],
  },
  {
    key: "enterprise",
    tier: "پیشنهاد ما برای سازمان‌ها",
    title: "پیشرفته",
    desc: "برای گروه‌های اقتصادی و شرکت‌های چندشعبه‌ای با نیاز به یکپارچه‌سازی و پشتیبانی اختصاصی",
    dotColor: "plan_pro_bg",
    priceYearly: "6,000,000",
    ribbon: "پیشنهاد سامانه",
    featured: true,
    dark: false,
    ctaLabel: "تماس با فروش",
    featuresHead: "همه امکانات پایه، به‌علاوه",
    features: [
      { text: "شرکت‌های فعال نامحدود", on: true },
      { text: "اتصال به API و یکپارچه‌سازی با ERP", on: true },
      { text: "کاربران و نقش‌های نامحدود", on: true },
      { text: "گزارش‌ساز اختصاصی و خروجی انبوه", on: true },
      { text: "کارشناس پشتیبانی اختصاصی", on: true },
      { text: "SLA و پشتیبان‌گیری اختصاصی", on: true },
    ],
  },
];

const COMPARE_ROWS = [
  { label: "تعداد صورتحساب سالانه", pro: "نامحدود", ent: "نامحدود" },
  { label: "تعداد شرکت فعال", pro: "۵", ent: "نامحدود" },
  { label: "تعداد کاربر هم‌زمان", pro: "۵", ent: "نامحدود" },
  { label: "صدور گواهی CSR", pro: true, ent: true },
  { label: "اتصال به API و ERP", pro: false, ent: true },
  { label: "پشتیبانی", pro: "تلفنی", ent: "کارشناس اختصاصی" },
];

const WHY_US = [
  {
    icon: LockIcon,
    title: "امنیت داده‌ها",
    desc: "رمزنگاری و پشتیبان‌گیری منظم از اطلاعات شرکت شما",
  },
  {
    icon: RepeatIcon,
    title: "به‌روزرسانی مداوم",
    desc: "هماهنگ با آخرین تغییرات مقررات سازمان امور مالیاتی",
  },
  {
    icon: ChatIcon,
    title: "پشتیبانی پاسخگو",
    desc: "همراهی تیم پشتیبانی در تمام مراحل کار با سامانه",
  },
];

// ----------------------------------------------------------------
// کارت پلن
// ----------------------------------------------------------------
function PlanCard({ plan, index }) {
  const priceNum = toNumber(plan.priceYearly);
  const monthlyEquivalent = priceNum
    ? formatToman(Math.round(priceNum / 12))
    : null;

  const cardBg = plan.dark ? "pricing_enterprise_bg" : "pricing_card_bg";
  const textPrimary = plan.dark
    ? "pricing_enterprise_text"
    : "pricing_text_primary";
  const textMuted = plan.dark
    ? "pricing_enterprise_text_muted"
    : "pricing_text_muted";
  const borderColor = plan.featured
    ? "pricing_card_border_featured"
    : plan.dark
      ? "pricing_enterprise_border"
      : "pricing_card_border";

  return (
    <MotionBox
      custom={index}
      w={"full"}
      variants={cardEntrance}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      position="relative"
      bg={cardBg}
      borderRadius="20px"
      border="1.5px solid"
      borderColor={borderColor}
      boxShadow={
        plan.featured
          ? "0 4px 10px rgba(20,30,30,.08), 0 20px 44px rgba(42,145,136,.16)"
          : "0 2px 4px rgba(20,30,30,.06), 0 10px 28px rgba(20,30,30,.08)"
      }
    >
      {/* لایه‌ی داخلی: فقط این overflow="hidden" داره تا شاین از کادر بیرون نزنه؛
          بج «پیشنهاد سامانه» بیرون از این لایه‌ست تا کامل روی لبه‌ی کارت دیده بشه */}
      <Box
        w={"full"}
        position="relative"
        overflow="hidden"
        borderRadius="18.5px"
        p={{ base: 6, md: 8 }}
        pt={9}
        display="flex"
        flexDirection="column"
      >
        {/* شاین دوره‌ای فقط روی کارت پیشنهادی */}
        {plan.featured && (
          <Box
            position="absolute"
            top={0}
            left="-120%"
            w="55%"
            h="100%"
            pointerEvents="none"
            bgGradient="linear(120deg, transparent, pricing_shine, transparent)"
            animation={`${shine} 4.5s ease-in-out infinite`}
          />
        )}

        <HStack spacing="8px" mb="10px">
          <Box w="9px" h="9px" borderRadius="full" bg={plan.dotColor} />
          <Text
            fontSize="13px"
            fontWeight="700"
            color={textMuted}
            letterSpacing="0.3px"
          >
            {plan.tier}
          </Text>
        </HStack>

        <Heading
          as="h3"
          fontSize="24px"
          fontWeight="800"
          color={textPrimary}
          mb="12px"
        >
          {plan.title}
        </Heading>

        <Text
          fontSize="13.5px"
          color={textMuted}
          lineHeight="1.8"
          mb="26px"
          minH="42px"
        >
          {plan.desc}
        </Text>

        <Box mb="4px" minH="46px">
          <AnimatePresence mode="wait">
            <MotionBox
              key={plan.key}
              variants={priceSwap}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.22 }}
            >
              <HStack align="baseline" spacing="7px">
                <Text
                  fontSize="34px"
                  fontWeight="800"
                  color={textPrimary}
                  fontVariantNumeric="tabular-nums"
                >
                  {plan.priceYearly}
                </Text>
                <Text fontSize="13px" color={textMuted} fontWeight="600">
                  تومان / سال
                </Text>
              </HStack>
            </MotionBox>
          </AnimatePresence>
        </Box>

        <Text
          fontSize="12.5px"
          color="pricing_text_faint"
          mb="20px"
          minH="16px"
        >
          {monthlyEquivalent && `معادل ${monthlyEquivalent} تومان در ماه`}
        </Text>

        <Button
          as={motion.button}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          w="100%"
          borderRadius="11px"
          fontWeight="700"
          fontSize="14.5px"
          mb="26px"
          h="46px"
          bg={plan.featured ? "pricing_accent_teal" : "pricing_surface_mint"}
          color={
            plan.featured
              ? "pricing_enterprise_text"
              : "pricing_accent_teal_dark"
          }
          border={!plan.featured ? "1.5px solid" : "none"}
          borderColor="pricing_surface_teal_tint"
          boxShadow={plan.featured ? "0 8px 18px rgba(42,145,136,.28)" : "none"}
          _hover={{
            bg: plan.featured
              ? "pricing_accent_teal_hover"
              : "pricing_surface_teal_tint",
          }}
        >
          <HStack justify="center" spacing="8px">
            <Text as="span">{plan.ctaLabel}</Text>
            <Icon as={ArrowLeftIcon} boxSize="12px" />
          </HStack>
        </Button>

        <Box h="1px" bg="pricing_card_border" mb="22px" />

        <Text fontSize="12.5px" fontWeight="700" color={textMuted} mb="16px">
          {plan.featuresHead}
        </Text>

        <VStack align="stretch" spacing="13px" flex="1">
          {plan.features.map((f, i) => (
            <MotionBox
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.05, duration: 0.3 }}
            >
              <HStack align="flex-start" spacing="10px">
                <Box
                  w="20px"
                  h="20px"
                  borderRadius="full"
                  bg={f.on ? "pricing_surface_teal_tint" : "transparent"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt="1px"
                >
                  <Icon
                    as={f.on ? CheckCircleIcon : SmallCloseIcon}
                    boxSize="13px"
                    color={
                      f.on ? "pricing_accent_teal_dark" : "pricing_text_faint"
                    }
                  />
                </Box>
                <Text
                  fontSize="13.5px"
                  color={f.on ? "pricing_text_secondary" : "pricing_text_faint"}
                  lineHeight="1.6"
                >
                  {f.text}
                </Text>
              </HStack>
            </MotionBox>
          ))}
        </VStack>
      </Box>

      {/* بج بیرون از لایه‌ی overflow:hidden، نسبت به کارت اصلی جای‌گذاری میشه
          تا نصفش که از لبه‌ی بالای کارت بیرون می‌زنه بریده نشه */}
      {plan.ribbon && (
        <Badge
          position="absolute"
          top="-14px"
          insetInlineEnd="28px"
          bg="pricing_accent_teal"
          color="pricing_enterprise_text"
          fontSize="12px"
          fontWeight="700"
          px="16px"
          py="7px"
          borderRadius="100px"
          boxShadow="0 4px 14px rgba(42,145,136,.4)"
          textTransform="none"
          animation={`${pulse} 2.4s ease-in-out infinite`}
        >
          <HStack spacing="4px">
            <Icon as={StarIcon} boxSize="10px" />
            <Text as="span">{plan.ribbon}</Text>
          </HStack>
        </Badge>
      )}
    </MotionBox>
  );
}

// ----------------------------------------------------------------
// کامپوننت اصلی
// ----------------------------------------------------------------
export default function PricingPlans() {
  return (
    <UserProfile>
      <BoxPanel mt="60px">
        <Box
          dir="rtl"
          bg="pricing_page_bg"
          minH="100vh"
          position="relative"
          overflow="hidden"
        >
          {/* دایره‌های تزئینی محو در پس‌زمینه‌ی هیرو، حس عمق و زندگی به صفحه می‌دن */}
          <Box
            position="absolute"
            top="-80px"
            insetInlineStart="-60px"
            w="280px"
            h="280px"
            borderRadius="full"
            bgGradient="radial(pricing_surface_teal_tint, transparent 70%)"
            filter="blur(10px)"
            animation={`${floatSlow} 8s ease-in-out infinite`}
            pointerEvents="none"
          />
          <Box
            position="absolute"
            top="120px"
            insetInlineEnd="-100px"
            w="360px"
            h="360px"
            borderRadius="full"
            bgGradient="radial(pricing_surface_teal_tint, transparent 70%)"
            filter="blur(10px)"
            animation={`${floatSlow} 10s ease-in-out infinite`}
            pointerEvents="none"
          />

          <Container maxW="1200px" py={{ base: 6, md: 10 }} position="relative">
            {/* هیرو */}
            <MotionBox
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <MotionVStack
                variants={heroStagger}
                initial="hidden"
                animate="visible"
                spacing="18px"
                textAlign="center"
                py={{ base: 6, md: 10 }}
              >
                <MotionBox variants={heroItem}>
                  <HStack
                    spacing="8px"
                    bg="pricing_surface_teal_tint"
                    color="pricing_accent_teal_hover"
                    fontSize="13px"
                    fontWeight="700"
                    px="16px"
                    py="7px"
                    borderRadius="100px"
                    animation={`${badgePulse} 2.4s ease-in-out infinite`}
                  >
                    <Icon as={StarIcon} boxSize="12px" />
                    <Text>پلن اشتراک سامانه</Text>
                  </HStack>
                </MotionBox>

                <MotionBox variants={heroItem}>
                  <Heading
                    as="h1"
                    fontSize={{ base: "27px", md: "36px" }}
                    fontWeight="800"
                    color="pricing_text_primary"
                    lineHeight="1.5"
                    maxW="680px"
                  >
                    به خانواده حرفه ای{" "}
                    <Text
                      as="span"
                      bgGradient="linear(to-l, pricing_accent_teal, pricing_accent_teal_dark)"
                      bgClip="text"
                    >
                      ایران مودی
                    </Text>{" "}
                   بپیوندید
                  </Heading>
                </MotionBox>
                <MotionBox variants={heroItem}>
                  <Text
                    fontSize="15.5px"
                    color="pricing_text_muted"
                    maxW="560px"
                    lineHeight="1.9"
                  >
                    از ثبت اولیه صورتحساب‌ها تا صدور خودکار گواهی CSR برای حجم
                    بالای طرف‌حساب‌ها؛ با رشد کسب‌وکار شما، سامانه هم رشد
                    می‌کند.
                  </Text>
                </MotionBox>
                
              </MotionVStack>
            </MotionBox>

            {/* کارت‌های پلن */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap="28px"
              mt="16px"
              w={"full"}
            >
              {PLAN_DATA.map((plan, i) => (
                <MotionGridItem
                  key={plan.key}
                  order={{ base: plan.featured ? -1 : 0, md: 0 }}
                >
                  <PlanCard plan={plan} index={i} />
                </MotionGridItem>
              ))}
            </Grid>

            {/* چرا سامانه‌ی ما؟ */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              mt="72px"
            >
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                {WHY_US.map((item, i) => (
                  <MotionBox
                    key={i}
                    whileHover={{ y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    bg="pricing_card_bg"
                    border="1px solid"
                    borderColor="pricing_card_border"
                    borderRadius="16px"
                    p="22px"
                    textAlign="center"
                  >
                    <Box
                      w="46px"
                      h="46px"
                      mx="auto"
                      mb="14px"
                      borderRadius="12px"
                      bg="pricing_surface_teal_tint"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        as={item.icon}
                        boxSize="20px"
                        color="pricing_accent_teal_dark"
                      />
                    </Box>
                    <Text
                      fontWeight="700"
                      fontSize="14.5px"
                      color="pricing_text_primary"
                      mb="6px"
                    >
                      {item.title}
                    </Text>
                    <Text
                      fontSize="13px"
                      color="pricing_text_muted"
                      lineHeight="1.8"
                    >
                      {item.desc}
                    </Text>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </MotionBox>

            {/* جدول مقایسه */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              mt="56px"
              bg="pricing_card_bg"
              borderRadius="18px"
              boxShadow="0 2px 4px rgba(20,30,30,.06), 0 10px 28px rgba(20,30,30,.08)"
              overflow="hidden"
            >
              <Box
                px="28px"
                py="22px"
                borderBottom="1px solid"
                borderColor="pricing_card_border"
              >
                <Heading
                  as="h2"
                  fontSize="18px"
                  fontWeight="800"
                  color="pricing_text_primary"
                >
                  مقایسه کامل امکانات
                </Heading>
                <Text fontSize="13px" color="pricing_text_muted" mt="4px">
                  جزئیات دقیق هر پلن را برای تصمیم‌گیری بهتر بررسی کنید
                </Text>
              </Box>

              <Box overflowX="auto">
                <Box as="table" w="100%" style={{ borderCollapse: "collapse" }}>
                  <Box as="thead">
                    <Box as="tr">
                      <Box
                        as="th"
                        textAlign="right"
                        color="pricing_text_muted"
                        fontSize="13px"
                        bg="pricing_surface_mint"
                        px="14px"
                        py="16px"
                        borderBottom="1px solid"
                        borderColor="pricing_card_border"
                      >
                        امکانات
                      </Box>
                      <Box
                        as="th"
                        textAlign="center"
                        color="pricing_text_secondary"
                        fontSize="13px"
                        bg="pricing_surface_mint"
                        px="14px"
                        py="16px"
                        borderBottom="1px solid"
                        borderColor="pricing_card_border"
                      >
                        پایه
                      </Box>
                      <Box
                        as="th"
                        textAlign="center"
                        color="pricing_text_secondary"
                        fontSize="13px"
                        bg="pricing_surface_mint"
                        px="14px"
                        py="16px"
                        borderBottom="1px solid"
                        borderColor="pricing_card_border"
                      >
                        پیشرفته
                      </Box>
                    </Box>
                  </Box>
                  <Box as="tbody">
                    {COMPARE_ROWS.map((row, i) => (
                      <Box
                        as="tr"
                        key={i}
                        _hover={{ bg: "pricing_surface_mint" }}
                        transition="background .15s ease"
                      >
                        <Box
                          as="td"
                          textAlign="right"
                          fontWeight="600"
                          color="pricing_text_primary"
                          fontSize="13.5px"
                          px="14px"
                          py="14px"
                          borderBottom={
                            i === COMPARE_ROWS.length - 1 ? "none" : "1px solid"
                          }
                          borderColor="pricing_card_border"
                        >
                          {row.label}
                        </Box>
                        {[row.pro, row.ent].map((val, j) => (
                          <Box
                            as="td"
                            key={j}
                            textAlign="center"
                            fontSize="13.5px"
                            color="pricing_text_secondary"
                            px="14px"
                            py="14px"
                            borderBottom={
                              i === COMPARE_ROWS.length - 1
                                ? "none"
                                : "1px solid"
                            }
                            borderColor="pricing_card_border"
                          >
                            {typeof val === "boolean" ? (
                              <Icon
                                as={val ? CheckCircleIcon : SmallCloseIcon}
                                boxSize="16px"
                                color={
                                  val
                                    ? "pricing_accent_teal"
                                    : "pricing_text_faint"
                                }
                              />
                            ) : (
                              val
                            )}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </MotionBox>

            {/* پانوشت */}
            <Text
              textAlign="center"
              mt="50px"
              fontSize="13.5px"
              color="pricing_text_muted"
              lineHeight="2"
            >
              قیمت‌ها شامل مالیات بر ارزش‌افزوده هستند و در هر زمان قابل ارتقا
              می‌باشند.
              <br />
              سوالی دارید؟{" "}
              <chakra.a
                href="#"
                color="pricing_accent_teal_hover"
                fontWeight="700"
              >
                با پشتیبانی سامانه مودیان در تماس باشید
              </chakra.a>
            </Text>
          </Container>
        </Box>
      </BoxPanel>
    </UserProfile>
  );
}
